import ExcelJS from "exceljs";

const TEAL_HDR = "28B3B4";
const TEAL_HDR2 = "1A8E8F";
const TEAL_TEXT = "085041";
const BORDER_CLR = "C8E8E6";
const ROW_EVEN_BG = "F0FAFA";
const ROW_ODD_BG = "FFFFFF";

const ALLOCATION_STATUS_LABELS = {
  FULLY_ALLOCATED: "Fully Allocated",
  PARTIAL: "Partially Allocated",
  IN_SCG_POOL: "In SCG Pool",
  NOT_ALLOCATED: "Not Allocated",
};

const COLUMN_DEFS = [
  { header: "Associate Name", key: "fullName", width: 26 },
  { header: "Employee ID", key: "employeeId", width: 16 },
  { header: "Level", key: "levelCode", width: 10 },
  { header: "Role Title", key: "levelName", width: 26 },
  { header: "Skills", key: "skills", width: 34 },
  { header: "Allocated FTE", key: "usedFte", width: 15 },
  { header: "Available FTE", key: "availableFte", width: 15 },
  { header: "Allocation Status", key: "allocationStatus", width: 20 },
  { header: "Current Position", key: "currentPosition", width: 24 },
  { header: "Current MAC", key: "currentMacName", width: 22 },
  { header: "Reporting Manager", key: "reportingManager", width: 24 },
  { header: "Contractor", key: "isContractor", width: 14 },
  { header: "Billable", key: "isBillable", width: 12 },
  { header: "Join Date", key: "joinDate", width: 15 },
];

function hairBorder(color = BORDER_CLR) {
  const s = { style: "hair", color: { argb: "FF" + color } };
  return { top: s, bottom: s, left: s, right: s };
}

function mediumBorder(color = TEAL_HDR2) {
  const m = { style: "medium", color: { argb: "FF" + color } };
  const t = { style: "thin", color: { argb: "FF" + color } };
  return { top: m, bottom: m, left: t, right: t };
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export async function exportAssociates(rows = [], activeFilter = "all") {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Psi360";
  wb.created = new Date();

  const ws = wb.addWorksheet("Associate Master", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  ws.columns = COLUMN_DEFS.map((c) => ({
    header: c.header,
    key: c.key,
    width: c.width,
  }));

  // ── Header row ─────────────────────────────────────────────────────────────
  const headerRow = ws.getRow(1);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Arial" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + TEAL_HDR } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = mediumBorder();
  });

  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: COLUMN_DEFS.length },
  };

  // ── Data rows ──────────────────────────────────────────────────────────────
  rows.forEach((rowData, idx) => {
    const excelRowIdx = idx + 2;
    const isEven = excelRowIdx % 2 === 0;
    const defaultBg = isEven ? ROW_EVEN_BG : ROW_ODD_BG;

    const row = ws.addRow({
      fullName: rowData.fullName ?? "",
      employeeId: rowData.employeeId ?? "",
      levelCode: rowData.levelCode ?? "",
      levelName: rowData.levelName ?? "",
      skills: Array.isArray(rowData.skills) ? rowData.skills.join(", ") : (rowData.skills ?? ""),
      usedFte: rowData.usedFte != null ? Number(rowData.usedFte) : 0,
      availableFte: rowData.availableFte != null ? Number(rowData.availableFte) : 0,
      allocationStatus: ALLOCATION_STATUS_LABELS[rowData.allocationStatus] ?? (rowData.allocationStatus ?? "—"),
      currentPosition: rowData.currentPosition ?? "—",
      currentMacName: rowData.currentMacName ?? "—",
      reportingManager: rowData.reportingManager ?? "—",
      isContractor: rowData.isContractor ? "Yes" : "No",
      isBillable: rowData.isbillable !== false ? "Yes" : "No",
      joinDate: fmtDate(rowData.joinDate),
    });

    row.height = 18;

    COLUMN_DEFS.forEach((col, colIdx) => {
      const cell = row.getCell(colIdx + 1);
      const isNum = col.key === "usedFte" || col.key === "availableFte";
      const isCenter = isNum || col.key === "levelCode" || col.key === "isContractor" || col.key === "isBillable" || col.key === "joinDate";

      cell.font = { name: "Arial", size: 10, color: { argb: "FF" + TEAL_TEXT } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + defaultBg } };
      cell.alignment = { horizontal: isCenter ? "center" : "left", vertical: "middle" };
      cell.border = hairBorder();

      if (isNum) {
        cell.numFmt = "0.00";
      }
    });
  });

  // ── Summary row ────────────────────────────────────────────────────────────
  const summaryRow = ws.addRow({
    fullName: `Total Associates: ${rows.length}`,
  });
  summaryRow.height = 20;
  summaryRow.getCell(1).font = { name: "Arial", size: 10, bold: true, color: { argb: "FF" + TEAL_TEXT } };
  summaryRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + ROW_EVEN_BG } };

  // ── Download ───────────────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const today = new Date().toISOString().split("T")[0];
  const filterSuffix = activeFilter && activeFilter !== "all" ? `_${activeFilter}` : "";
  a.download = `associate_master${filterSuffix}_${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
